import React, { PureComponent } from 'react';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import { connect } from 'react-redux';
import { bool, func, any, string, number } from 'prop-types';
import numeral from 'numeral';
import Radium from 'radium';
import { isEmpty } from '@widergy/web-utils/lib/array';
import { validateDependantField, lengthEquals, pattern } from '@widergy/web-utils/lib/validate';
import { UTTextInput, UTButton, UTLabel, AlertHandler, WithLoading } from '@widergy/energy-ui';
import { getIn } from 'seamless-immutable';

import { normalizeAccount } from 'utils/formatUtils';
import { appConfig } from 'config/customization';
import UTSelectInput from 'app/components/UTSelectInput';
import UTCheckbox from 'app/components/UTCheckbox';
import { message } from 'utils/stringUtils';
import { ACCOUNT_HOLDER } from 'constants/reduxForms';
import UTForm from 'app/components/UTForm';

import AccountDebtTable from './components/AccountDebtTable';
import { accountDebtColumns } from './components/AccountDebtTable/constants';
import {
  registerAccountFields,
  WARRANTY_CHARGE_POSESSIONS,
  DESTINATION_COMERCE,
  DESTINATION_CONSTRUCTION,
  SELECT,
  ACCOUNT_NUMBER,
  ACCOUNT_NUMBER_SELECT,
  AC_CREDIT_POSSESSION
} from './constants';
import { getAccountDebtConcepts, getAccountDueConcepts, getTotalAmount } from './registerAccountHelper';
import styles from './styles';

class RegisterAccount extends PureComponent {
  state = { termsChecked: false, disableSubmit: false };

  accountNumberValidationSelect = [
    validateDependantField(ACCOUNT_NUMBER, message('Accounts:accountRequired')),
    lengthEquals(
      appConfig.ACCOUNT_LENGTH,
      message('Accounts:lengthValidation', {
        count: appConfig.ACCOUNT_LENGTH
      })
    ),
    pattern(appConfig.clientNumberRegex, message('Validations:invalidFormat'))
  ];

  accountNumberValidation = [
    validateDependantField(ACCOUNT_NUMBER_SELECT, message('Accounts:accountRequired')),
    lengthEquals(
      appConfig.ACCOUNT_LENGTH,
      message('Accounts:lengthValidation', {
        count: appConfig.ACCOUNT_LENGTH
      })
    ),
    pattern(appConfig.clientNumberRegex, message('Validations:invalidFormat'))
  ];

  componentDidUpdate = prevProps => {
    if (this.props.supplyDestination && this.props.supplyDestination !== prevProps.supplyDestination) {
      if (this.props.supplyDestination === DESTINATION_COMERCE) {
        AlertHandler.error(message('RegisterAccount:commercialHolderChangeError'), false, 10000);
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ disableSubmit: true });
      } else if (this.props.supplyDestination === DESTINATION_CONSTRUCTION) {
        AlertHandler.error(message('RegisterAccount:othersHolderChangeError'), false, 10000);
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ disableSubmit: true });
        // eslint-disable-next-line react/no-did-update-set-state
      } else this.setState({ disableSubmit: false });
    }
  };

  getAccreditPossesionValue = fields =>
    fields
      .map(field => field.key === 'accreditPossession' && `id${field.id}`)
      .filter(item => item)
      .find(item => item);

  handleFieldChange = fieldType => val => {
    this.props.dispatch(
      change(ACCOUNT_HOLDER, fieldType === SELECT ? ACCOUNT_NUMBER : ACCOUNT_NUMBER_SELECT, null)
    );
    return val;
  };

  includesWarrantyCharge = fields =>
    WARRANTY_CHARGE_POSESSIONS.indexOf(
      this.props.accreditPossession[this.getAccreditPossesionValue(fields)]
    ) >= 0;

  handleOnTermsCheck = () => this.setState(prevState => ({ termsChecked: !prevState.termsChecked }));

  render() {
    const fields = registerAccountFields;
    const filteredAccountList = this.props.accountList.filter(account => account.new_service_enabled);
    const { disableSubmit, termsChecked } = this.state;
    return (
      <form onSubmit={this.props.handleSubmit} style={styles.container}>
        <div style={styles.titleContainer}>
          <UTLabel large extrabold style={styles.title}>
            {message('RegisterAccount:accountHolderChangeTitle')}
          </UTLabel>
          <UTLabel style={styles.title}>{message('RegisterAccount:accountHolderChangeSubtitle')}</UTLabel>
        </div>
        <div style={styles.field}>
          {!isEmpty(filteredAccountList) && !this.props.clientNumber && (
            <div style={styles.registerSelectField}>
              <UTLabel style={styles.helpText}>{message('RegisterAccount:select')}</UTLabel>
              <Field
                name="accountNumberSelect"
                placeholder={message('RegisterAccount:supply')}
                component={UTSelectInput}
                keyProp="id"
                disabled={!!this.props.clientNumber}
                onChangeText={this.handleFieldChange(SELECT)}
                labelKey="client_number"
                valueKey="client_number"
                validate={!this.props.textFieldValue && this.accountNumberValidationSelect}
                options={filteredAccountList}
              />
            </div>
          )}
          <UTLabel style={styles.helpText}>{message('RegisterAccount:enterNIS')}</UTLabel>
          <Field
            component={UTTextInput}
            name="accountNumber"
            disabled={!!this.props.clientNumber}
            onChangeText={this.handleFieldChange()}
            placeholder={message('RegisterAccount:clientNumberInput', {
              accountLength: appConfig.ACCOUNT_LENGTH
            })}
            validate={!this.props.selectFieldValue && this.accountNumberValidation}
            normalize={normalizeAccount}
          />
        </div>
        {!this.props.shouldShowRegisterAccountForm && (
          <UTButton
            disabled={this.props.submitFailed && this.props.invalid}
            style={styles.continueButton}
            type="submit"
          >
            {message('RegisterAccount:continue')}
          </UTButton>
        )}
        {this.props.shouldShowRegisterAccountForm && (
          <div>
            <div style={styles.formContainer}>
              <UTForm fields={fields} fieldValues={this.props.fieldValues} replaceFieldNames />
            </div>
            <div style={styles.checkContainer}>
              <UTCheckbox onChange={this.handleOnTermsCheck} value={termsChecked} />
              <UTLabel small>
                {message('RegisterAccount:checkLabel', { regulationURL: this.props.regulationURL })}
              </UTLabel>
            </div>
            <UTLabel small style={styles.checkContainer}>
              {message('RegisterAccount:userDataLabel')}
            </UTLabel>
            <div>
              <UTLabel extrabold style={{ ...styles.title, ...styles.checkContainer, ...styles.warranties }}>
                {message('RegisterAccount:nextBillCharge')}
              </UTLabel>
              <UTLabel style={styles.checkContainer}>
                {message('RegisterAccount:nextBillChargeContent')}
              </UTLabel>
              {this.includesWarrantyCharge(fields) && (
                <UTLabel style={styles.checkContainer}>
                  {message('RegisterAccount:warrantyCharge', {
                    amount: numeral(this.props.warrantyCharge).format('$ 0.[00]')
                  })}
                </UTLabel>
              )}
            </div>

            <div style={styles.table}>
              <div style={styles.tableContainer}>
                <AccountDebtTable
                  title={message('RegisterAccount:clientDebt', {
                    supply: this.props.clientNumber
                  })}
                  concepts={getAccountDebtConcepts(
                    this.props.accountTotalDebt && this.props.accountTotalDebt.Recibos
                  )}
                  tableColumns={accountDebtColumns}
                />
              </div>
              <div style={styles.tableContainer}>
                <AccountDebtTable
                  title={message('RegisterAccount:ownDebt', {
                    holder: this.props.currentAccountHolderName
                  })}
                  concepts={getAccountDueConcepts(
                    this.props.accountDueDebt && this.props.accountDueDebt.Deuda
                  )}
                  tableColumns={accountDebtColumns}
                />
              </div>
              <UTLabel extrabold style={styles.totalDebt}>
                {message('RegisterAccount:userDebt', {
                  debt: `$ ${getTotalAmount(this.props.accountTotalDebt, this.props.accountDueDebt).toFixed(
                    2
                  )}`
                })}
              </UTLabel>
            </div>
            {getTotalAmount(this.props.accountTotalDebt, this.props.accountDueDebt) !== 0 && (
              <UTLabel style={styles.userDebitCancel}>{message('RegisterAccount:userDebtCancel')}</UTLabel>
            )}
            <UTButton
              style={styles.continueButton}
              type="submit"
              disabled={!termsChecked || (this.props.submitFailed && this.props.invalid) || disableSubmit}
            >
              {message('RegisterAccount:createProcedure')}
            </UTButton>
          </div>
        )}
      </form>
    );
  }
}

RegisterAccount.propTypes = {
  handleSubmit: func.isRequired,
  fieldValues: any, // eslint-disable-line react/forbid-prop-types
  shouldShowRegisterAccountForm: bool,
  accountTotalDebt: any, // eslint-disable-line
  accountDueDebt: any, // eslint-disable-line
  clientNumber: string,
  currentAccountHolderName: string,
  supplyDestination: string,
  accreditPossession: any, // eslint-disable-line react/forbid-prop-types
  submitFailed: bool,
  invalid: bool,
  warrantyCharge: number,
  selectFieldValue: string,
  textFieldValue: string,
  regulationURL: string,
  accountList: any // eslint-disable-line react/forbid-prop-types
};

const form = reduxForm({ form: ACCOUNT_HOLDER, shouldError: () => true })(Radium(RegisterAccount));
const formSelector = formValueSelector(ACCOUNT_HOLDER);

const mapStateToProps = store => ({
  fieldValues: formSelector(store, ...registerAccountFields.map(field => `id${field.id}`)),
  accreditPossession: formSelector(
    store,
    ...registerAccountFields.map(field => field.key === AC_CREDIT_POSSESSION && `id${field.id}`)
  ),
  supplyDestination: formSelector(store, 'id2'),
  selectFieldValue: formSelector(store, ACCOUNT_NUMBER_SELECT),
  textFieldValue: formSelector(store, ACCOUNT_NUMBER),
  accountList: store.account.accounts,
  regulationURL: getIn(store, ['utility', 'appConfiguration', 'supply_regulation_docs_url'])
});

export default WithLoading(props => props.loading)(connect(mapStateToProps)(form));
